import { lazy, Suspense, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';

import CookieConsent, { isAnalyticsAllowed } from './components/CookieConsent';
import Layout from './components/Layout';
import RootRedirect from './components/RootRedirect';
import ScrollToTop from './components/common/ScrollToTop';
import { ErrorBoundary } from './components/errors';
import { PageLoadingSkeleton } from './components/ui/Skeletons';
import { initGADeferred, trackPageView } from './lib/analytics';
import { LanguageProvider } from './providers/LanguageProvider';

// Lazy load pages for code splitting
const Home = lazy(() => import('./pages/Home'));
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const Track = lazy(() => import('./pages/Track'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));

// Error pages
const NotFound = lazy(() => import('./pages/NotFound'));
const ServerError = lazy(() => import('./pages/ServerError'));

// Legal pages
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));

// Demo pages
const MockupDemo = lazy(() => import('./pages/MockupDemo'));
const TshirtDesigner = lazy(() => import('./pages/TshirtDesigner'));

// GA is initialized lazily after GDPR consent, not at module load

// Disable browser scroll restoration as early as possible — before any React
// rendering — so iOS Safari doesn't restore scroll on the first navigation.
if ('scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

// Route change tracker (must be inside BrowserRouter)
// Scroll-to-top is handled by <ScrollToTop /> mounted alongside this component.
function RouteChangeTracker() {
  const location = useLocation();
  useEffect(() => {
    if (isAnalyticsAllowed()) {
      trackPageView(location.pathname, document.title);
    }
  }, [location.pathname]);
  return null;
}

function PrefixedRoutes() {
  // Bulgarian-language route slugs below; English slugs are aliased on the
  // following line for the EN locale. i18n-ignore: these are URL paths, not
  // user-facing copy — they appear in the address bar and SEO sitemap only.
  return (
    <Routes>
      {/* Full-page designer — outside Layout (no header/footer) */}
      <Route
        path="дизайн/:productSlug" // i18n-ignore: route slug
        element={<TshirtDesigner />}
      />
      <Route path="design/:productSlug" element={<TshirtDesigner />} />

      <Route path="/" element={<Layout />}>
        <Route index element={<Home />} />

        <Route
          path="продукти" // i18n-ignore: route slug
          element={<Products />}
        />
        <Route path="products" element={<Products />} />

        <Route
          path="продукт/:id" // i18n-ignore: route slug
          element={<ProductDetail />}
        />
        <Route path="product/:id" element={<ProductDetail />} />

        <Route
          path="количка" // i18n-ignore: route slug
          element={<Cart />}
        />
        <Route path="cart" element={<Cart />} />

        <Route
          path="поръчка" // i18n-ignore: route slug
          element={<Checkout />}
        />
        <Route path="checkout" element={<Checkout />} />

        <Route
          path="потвърждение/:orderNumber" // i18n-ignore: route slug
          element={<OrderConfirmation />}
        />
        <Route
          path="order-confirmation/:orderNumber"
          element={<OrderConfirmation />}
        />

        <Route
          path="любими" // i18n-ignore: route slug
          element={<Wishlist />}
        />
        <Route path="wishlist" element={<Wishlist />} />

        <Route
          path="проследяване" // i18n-ignore: route slug
          element={<Track />}
        />
        <Route path="track" element={<Track />} />

        <Route
          path="за-нас" // i18n-ignore: route slug
          element={<About />}
        />
        <Route path="about" element={<About />} />

        <Route
          path="контакти" // i18n-ignore: route slug
          element={<Contact />}
        />
        <Route path="contact" element={<Contact />} />

        <Route
          path="общи-условия" // i18n-ignore: route slug
          element={<TermsOfService />}
        />
        <Route path="terms" element={<TermsOfService />} />

        <Route
          path="поверителност" // i18n-ignore: route slug
          element={<PrivacyPolicy />}
        />
        <Route path="privacy" element={<PrivacyPolicy />} />

        <Route
          path="бисквитки" // i18n-ignore: route slug
          element={<CookiePolicy />}
        />
        <Route path="cookies" element={<CookiePolicy />} />

        <Route
          path="политика-за-връщане" // i18n-ignore: route slug
          element={<RefundPolicy />}
        />
        <Route path="refund" element={<RefundPolicy />} />

        <Route
          path="дизайнер" // i18n-ignore: route slug
          element={<MockupDemo />}
        />
        <Route path="mockup-demo" element={<MockupDemo />} />

        <Route
          path="грешка" // i18n-ignore: route slug
          element={<ServerError />}
        />
        <Route path="error" element={<ServerError />} />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function App() {
  const { t } = useTranslation();
  // Initialize GA only if user has previously consented. Deferred so
  // GTM's ~135 KB transfer + main-thread work stays out of the LCP window.
  useEffect(() => {
    if (isAnalyticsAllowed()) {
      initGADeferred();
    }
  }, []);

  // On every internal link click: save current #root scroll to history state
  // (so back navigation can restore it), then reset #root.scrollTop to 0.
  useEffect(() => {
    const onLinkClick = (e: MouseEvent) => {
      const anchor = (e.target as Element).closest('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href') ?? '';
      if (
        !href ||
        href.startsWith('http') ||
        href.startsWith('//') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        href.startsWith('#')
      )
        return;
      const root = document.getElementById('root');
      // Save the real scroll position: on mobile root scrolls, on desktop window scrolls.
      const savedScroll = Math.max(root?.scrollTop ?? 0, window.scrollY);
      window.history.replaceState(
        {
          ...(window.history.state as object | null),
          _st: savedScroll,
        },
        ''
      );
      if (root) root.scrollTop = 0;
      window.scrollTo(0, 0);
    };
    document.addEventListener('click', onLinkClick, true);
    return () => document.removeEventListener('click', onLinkClick, true);
  }, []);

  const handleConsentChange = (preferences: { analytics: boolean }) => {
    if (preferences.analytics) {
      initGADeferred();
      trackPageView(window.location.pathname, document.title);
    }
  };

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 4000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
          <ScrollToTop />
          <RouteChangeTracker />
          <Suspense
            fallback={<PageLoadingSkeleton message={t('common.loading')} />}
          >
            <Routes>
              {/* Root: redirect to language-prefixed home based on cookie/browser */}
              <Route path="/" element={<RootRedirect />} />

              {/* Language-prefixed routes. The :lang/* pattern also matches
                  legacy unprefixed URLs (e.g. /products); LanguageProvider
                  detects an unsupported :lang segment and redirects to the
                  resolved language prefix while preserving the original path. */}
              <Route
                path=":lang/*"
                element={
                  <LanguageProvider>
                    <PrefixedRoutes />
                  </LanguageProvider>
                }
              />
            </Routes>
          </Suspense>
          {/* GDPR Cookie Consent Banner */}
          <CookieConsent onConsentChange={handleConsentChange} />
        </BrowserRouter>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
